// ============================================================================
// Beacon — plans & feature gating. Four tiers along the land-and-expand journey:
// Scout (free) → Operator (solo) → Firm (team) → Syndicate (raising capital),
// with Beacon Academy as an add-on (bundled free in Firm & Syndicate).
//
// v1: the active plan is stored locally (owner-settable in Admin) so gating can
// be demoed and previewed. Real billing (Stripe) + per-tenant plans come next.
// ============================================================================

export type PlanId = "scout" | "operator" | "firm" | "syndicate";

export interface Plan {
  id: PlanId;
  name: string;
  price: number; // monthly $
  tagline: string;
  who: string;
  academyIncluded: boolean;
  features: string[]; // headline unlocks for the pricing page
}

export const PLANS: Plan[] = [
  { id: "scout", name: "Scout", price: 0, tagline: "Analyze any deal, free forever.", who: "Learners & the curious", academyIncluded: false,
    features: ["Full underwriting engine", "Four compliance gates", "78-point due-diligence matrix", "Negotiation worksheet", "Glossary & community (read-only)"] },
  { id: "operator", name: "Operator", price: 99, tagline: "Run deals like a fund of one.", who: "The solo investor", academyIncluded: false,
    features: ["Everything in Scout", "Cloud sync & saved deals", "AI deal analyst + AI offer letters", "Address auto-fill & government data", "Bulk deal finder & saved buy boxes", "30-yr projections + growth snowball"] },
  { id: "firm", name: "Firm", price: 299, tagline: "Your whole crew, one workspace.", who: "Investor + team", academyIncluded: true,
    features: ["Everything in Operator", "Team seats — contractors & inspectors", "Native CRM: pipeline + contacts", "GoHighLevel sync + branded email", "Own-your-data export · white-label", "Beacon Academy included"] },
  { id: "syndicate", name: "Syndicate", price: 599, tagline: "Raise capital, do bigger deals.", who: "Sponsors raising capital", academyIncluded: true,
    features: ["Everything in Firm", "Syndicate module — unlimited members", "Cap table + sweat equity + 506(b) doc", "Community deal-leads & priority listings", "Members join free — sponsor pays", "Beacon Academy included"] },
];

export const ACADEMY_ADDON = 29; // $/user/mo for Operator (free in Firm & Syndicate)

const RANK: Record<PlanId, number> = { scout: 0, operator: 1, firm: 2, syndicate: 3 };

/** The gateable feature keys and the minimum tier that unlocks each. */
export type Feature = "cloud" | "ai" | "data" | "finder" | "team" | "crm" | "ghl" | "export" | "syndicate" | "academy";
const MIN_TIER: Record<Feature, PlanId> = {
  cloud: "operator", ai: "operator", data: "operator", finder: "operator", academy: "operator",
  team: "firm", crm: "firm", ghl: "firm", export: "firm",
  syndicate: "syndicate",
};

const FEATURE_LABEL: Record<Feature, string> = {
  cloud: "Cloud sync", ai: "AI analyst & offer letters", data: "Address auto-fill & data feeds",
  finder: "Bulk deal finder", academy: "Beacon Academy", team: "Team portal", crm: "Native CRM",
  ghl: "GoHighLevel sync", export: "Data export", syndicate: "Syndicate module",
};

const LS = "beacon_plan";
export function currentPlan(): PlanId {
  try { const p = localStorage.getItem(LS) as PlanId | null; if (p && p in RANK) return p; } catch { /* */ }
  return "syndicate"; // owner default: everything unlocked (downgrade in Admin to preview gating)
}
export function setPlan(id: PlanId) { try { localStorage.setItem(LS, id); } catch { /* */ } }
export function planOf(id: PlanId): Plan { return PLANS.find((p) => p.id === id)!; }

export function hasFeature(f: Feature): boolean { return RANK[currentPlan()] >= RANK[MIN_TIER[f]]; }
/** The tier a feature requires (for upsell copy). */
export function requiredPlan(f: Feature): Plan { return planOf(MIN_TIER[f]); }
export function featureLabel(f: Feature): string { return FEATURE_LABEL[f]; }
